/*****************************************************************
 *  CUSTOMER BUTLER - ONE LIST PAGE BODY (customers or properties) *
 *  File: apps/web/src/pages/customer/RecordList.js                *
 *  What it does: the search box, Zoho's saved views, the sort,    *
 *  the list in Zoho's columns, and paging - the same for the      *
 *  customer list and the property list. The views, columns and    *
 *  sorts come from the layout door (one layout file on the        *
 *  server); this file only draws them.                            *
 *  Reads: the list door it is handed (fetchPage).                 *
 *  Writes: nothing.                                               *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - READING ONE CELL                                   *
 *  SECTION 2 - ONE ROW                                            *
 *  SECTION 3 - THE LIST'S STATE (shared) AND THE CLEAN LIST       *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { QuietLine, Notice, StatusChip } from './customerParts';
import { showFieldValue } from './fieldValues';

export const ROWS_PER_PAGE = 50; // one page of a list
const SEARCH_PAUSE_MS = 300;  // wait this long after the last key before searching

/*****************************************************************
 *  SECTION 1 - READING ONE CELL                                   *
 *****************************************************************/

/* ---- readCellWords(row, column) --------------------------------
   A column from "builtin" reads the row itself; from "custom" it
   reads the row's custom field. Out: the words ('' when blank).  */
export function readCellWords(row, column) {
  const isBuiltin = column.from === 'builtin';
  const customValues = row.customFields || {};
  const rawValue = isBuiltin ? row[column.key] : customValues[column.key];
  const cellField = { kind: isBuiltin ? 'builtin' : 'custom', key: column.key, dataType: 'text' };

  return showFieldValue(cellField, rawValue);
}

/*****************************************************************
 *  SECTION 2 - ONE ROW                                            *
 *****************************************************************/

/* ---- ListRow({ row, columns, linkFor, nameFallback }) ----------
   The whole row is one link. The first column is the name; a row
   saved on this device but not yet synced carries the one chip.  */
function ListRow({ row, columns, linkFor, nameFallback }) {
  const isWaitingToSync = row._syncStatus === 'pending';
  const rowLink = isWaitingToSync ? null : linkFor(row);

  const cells = columns.map((oneColumn, columnIndex) => {
    const isNameColumn = columnIndex === 0;
    const cellWords = readCellWords(row, oneColumn);
    const shownWords = isNameColumn && !cellWords ? nameFallback(row) : cellWords;
    const cellClass = [
      'cb-cell',
      isNameColumn ? 'is-name' : '',
      shownWords ? '' : 'is-blank',
    ].filter(Boolean).join(' ');

    return (
      <span key={oneColumn.key} className={cellClass}>
        <span className="cb-hidden-label">{oneColumn.label}: </span>
        {shownWords || '—'}
      </span>
    );
  });

  const syncChip = isWaitingToSync ? (
    <span className="cb-row-chip"><StatusChip tone="warn" icon="offline" word="Not synced yet" /></span>
  ) : null;

  if (!rowLink) {
    return <li className="cb-row"><div className="cb-row-link">{cells}{syncChip}</div></li>;
  }

  return (
    <li className="cb-row">
      <Link className="cb-row-link" to={rowLink}>{cells}{syncChip}</Link>
    </li>
  );
}

/*****************************************************************
 *  SECTION 3 - THE CONTROLS AND THE LIST                          *
 *****************************************************************/

/* ---- useListPages(listParts) ------------------------------------
   The list's state and its one door call, shared by the clean list
   below and the HR look's list (HrRecordList.js, 2026-10-15), so the
   search pause, views, sorting, paging and the no-signal copy are
   written once.
   In: { layout, fetchPage(listChoice), fetchOffline (optional),
         nounMany, startPerPage (optional, 2026-10-15) }.
   Out: everything the list draws, plus the setters it needs.
   2026-10-15 (Zoho tour frame 080): "Records per page" and the
   field filters of Zoho's left rail travel with the same state;
   the field filters are sent to the door as listChoice.filters.  */
export function useListPages(listParts) {
  const { layout, fetchPage, fetchOffline, nounMany } = listParts;

  const defaultView = (layout.listViews.find((oneView) => oneView.isDefault) || layout.listViews[0]).key;

  const [typedSearch, setTypedSearch] = useState('');   // what is in the box right now
  const [search, setSearch] = useState('');             // what was last searched for
  const [view, setView] = useState(defaultView);
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);               // null = still loading
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [isOfflineCopy, setIsOfflineCopy] = useState(false);
  const [loadProblem, setLoadProblem] = useState('');
  const [perPage, setPerPage] = useState(listParts.startPerPage || ROWS_PER_PAGE); // "Records per page"
  const [fieldFilters, setFieldFilters] = useState([]);                           // [{ key, label, contains }]

  // ---- search a moment after the last key, back to page 1 ----
  useEffect(() => {
    const pauseTimer = setTimeout(() => {
      setSearch(typedSearch.trim());
      setPage(1);
    }, SEARCH_PAUSE_MS);

    return () => clearTimeout(pauseTimer);
  }, [typedSearch]);

  // ---- load one page from the door, or the device copy offline ----
  const loadPage = useCallback(async () => {
    setLoadProblem('');

    try {
      const pageAnswer = await fetchPage({ search, view, sort, page, limit: perPage, filters: fieldFilters });

      setRows(pageAnswer.rows);
      setMeta(pageAnswer.meta);
      setIsOfflineCopy(false);
    } catch (loadError) {
      if (fetchOffline) {
        const offlineRows = await fetchOffline(search);

        setRows(offlineRows);
        setMeta({ total: offlineRows.length, totalPages: 1 });
        setIsOfflineCopy(true);
        return;
      }

      setRows([]);
      setLoadProblem(`The ${nounMany} could not be loaded. Check the connection and try again.`);
    }
  }, [fetchPage, fetchOffline, search, view, sort, page, perPage, fieldFilters, nounMany]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  /* ---- chooseView(viewKey) / chooseSort(sortKey) -- back to page 1 ---- */
  const chooseView = useCallback((viewKey) => {
    setView(viewKey);
    setPage(1);
  }, []);

  const chooseSort = useCallback((sortKey) => {
    setSort(sortKey);
    setPage(1);
  }, []);

  /* ---- choosePerPage(count) / applyFieldFilters(list) -- back to page 1 ---- */
  const choosePerPage = useCallback((rowCount) => {
    setPerPage(rowCount);
    setPage(1);
  }, []);

  const applyFieldFilters = useCallback((filterList) => {
    setFieldFilters(filterList);
    setPage(1);
  }, []);

  const isMailingLabels = view === 'mailing-labels';
  const columns = isMailingLabels && layout.mailingLabelColumns.length > 0
    ? layout.mailingLabelColumns
    : layout.listColumns;

  const totalCount = Number(meta.total) || 0;
  const totalPages = Math.max(1, Number(meta.totalPages) || 1);

  return {
    typedSearch, setTypedSearch, search, view, chooseView, sort, chooseSort,
    page, setPage, rows, isOfflineCopy, loadProblem, columns, totalCount, totalPages,
    perPage, choosePerPage, fieldFilters, applyFieldFilters,
  };
}

/* ---- RecordList(listParts) -------------------------------------
   In: { layout (from the layout door), fetchPage(listChoice),
         fetchOffline (optional, for no signal), linkFor(row),
         nameFallback(row), searchHint, nounOne, nounMany }.
   Out: the list body.                                           */
export function RecordList(listParts) {
  const { layout, linkFor, nameFallback, searchHint, nounOne, nounMany } = listParts;

  const {
    typedSearch, setTypedSearch, search, view, chooseView, sort, chooseSort,
    page, setPage, rows, isOfflineCopy, loadProblem, columns, totalCount, totalPages,
  } = useListPages(listParts);

  const countWords = `${totalCount} ${totalCount === 1 ? nounOne : nounMany}`;

  return (
    <>
      <div className="cb-tools">
        <label className="cb-search-wrap">
          <span className="cb-hidden-label">Search</span>
          <Icon name="search" />
          <input
            className="cb-input cb-search"
            type="search"
            value={typedSearch}
            placeholder={searchHint}
            onChange={(changeEvent) => setTypedSearch(changeEvent.target.value)}
          />
        </label>
        <label>
          <span className="cb-hidden-label">View</span>
          <select className="cb-select" value={view} onChange={(changeEvent) => chooseView(changeEvent.target.value)}>
            {layout.listViews.map((oneView) => (
              <option key={oneView.key} value={oneView.key}>{oneView.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="cb-hidden-label">Sort</span>
          <select className="cb-select" value={sort} onChange={(changeEvent) => chooseSort(changeEvent.target.value)}>
            {layout.sorts.map((oneSort) => (
              <option key={oneSort.key} value={oneSort.key}>{oneSort.label}</option>
            ))}
          </select>
        </label>
      </div>

      {isOfflineCopy ? (
        <Notice tone="warn" icon="offline">No signal: showing the copy saved on this device. Views and sorting come back with the connection.</Notice>
      ) : null}
      {loadProblem ? <Notice tone="bad" icon="warn">{loadProblem}</Notice> : null}

      {rows === null ? <QuietLine icon="clock">Loading the {nounMany}</QuietLine> : null}

      {rows !== null && rows.length === 0 && !loadProblem ? (
        <QuietLine icon="search">{search ? `No ${nounMany} match "${search}".` : `No ${nounMany} in this view yet.`}</QuietLine>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <>
          <p className="cb-count">{countWords}</p>
          <div className="cb-list is-five-columns">
            <div className="cb-list-head" aria-hidden="true">
              {columns.map((oneColumn) => <span key={oneColumn.key}>{oneColumn.label}</span>)}
            </div>
            <ul className="cb-list-rows" aria-label={nounMany}>
              {rows.map((oneRow) => (
                <ListRow key={oneRow.id} row={oneRow} columns={columns} linkFor={linkFor} nameFallback={nameFallback} />
              ))}
            </ul>
          </div>
          {totalPages > 1 ? (
            <div className="cb-pager">
              <button type="button" className="bc-btn is-ghost" disabled={page <= 1} onClick={() => setPage((pageNow) => pageNow - 1)}>
                <Icon name="back" /> Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" className="bc-btn is-ghost" disabled={page >= totalPages} onClick={() => setPage((pageNow) => pageNow + 1)}>
                Next <Icon name="chevron" />
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

export default RecordList;
